// components/DrawingGallery.tsx
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaTimes, 
  FaDownload, 
  FaTrash, 
  FaUser, 
  FaCalendar, 
  FaPaintBrush,
  FaExpand,
  FaArrowLeft
} from 'react-icons/fa';
import { SavedDrawing } from '../types/canvas';
import { db, storage } from '../firebaseConfig';
import { doc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';

interface DrawingGalleryProps {
  drawings: SavedDrawing[];
  onBack: () => void;
}

export const DrawingGallery = ({ drawings, onBack }: DrawingGalleryProps) => {
  const [selectedDrawing, setSelectedDrawing] = useState<SavedDrawing | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  const handleDeleteDrawing = async (drawing: SavedDrawing) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar "${drawing.name}"?`)) return;

    setIsDeleting(drawing.id);

    try {
      // Eliminar de Firestore (imageUrl is a data URL, not stored in Storage)
      await deleteDoc(doc(db, 'savedDrawings', drawing.id));
    } catch (error) {
      console.error('Error eliminando dibujo:', error);
    } finally {
      setIsDeleting(null);
    }
  };

  const downloadImage = (url: string, name: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${name}.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Fecha desconocida';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="h-full flex flex-col  relative overflow-hidden">
      {/* Header mejorado */}
      <div className="bg-white/80 backdrop-blur-md border-b border-white/20 shadow-lg">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={onBack}
            className="p-3 bg-gradient-to-r from-purple-500 to-primary text-white rounded-xl hover:from-secondary hover:to-primary transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          
          <div className="text-center">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-primary bg-clip-text text-transparent">
              🎨 Galería de Arte
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {drawings.length} {drawings.length === 1 ? 'obra maestra' : 'obras maestras'}
            </p>
          </div>
          
          <div className="w-[52px]" /> {/* Spacer para centrar el título */}
        </div>
      </div>

      {/* Contenido de la galería */}
      <div className="flex-1 overflow-auto ">
        {drawings.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-8 h-full">
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl flex items-center justify-center">
                <FaPaintBrush className="w-16 h-16 text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-700 mb-3">Canvas en blanco</h3>
              <p className="text-gray-500 max-w-sm mx-auto leading-relaxed">
                Aún no hay obras de arte guardadas. ¡Crea tu primera masterpiece y guárdala aquí! 🎨
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {drawings.map((drawing) => (
                <motion.div
                  key={drawing.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-white/30"
                >
                  {/* Imagen del dibujo */}
                  <div className="relative aspect-video bg-gradient-to-br from-gray-50 to-gray-100">
                    <img
                      src={drawing.thumbnailUrl || drawing.imageUrl}
                      alt={drawing.name}
                      className="w-full h-full object-contain cursor-pointer hover:scale-105 transition-transform duration-300"
                      onClick={() => setSelectedDrawing(drawing)}
                    />
                    
                    {/* Overlay con acciones */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                      <button
                        onClick={() => setSelectedDrawing(drawing)}
                        className="p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-200 shadow-lg transform hover:scale-110"
                      >
                        <FaExpand className="w-4 h-4 text-gray-700" />
                      </button>
                    </div>
                  </div>

                  {/* Información del dibujo */}
                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 mb-3 truncate text-lg">
                      {drawing.name}
                    </h3>
                    
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2">
                        <FaUser className="w-3 h-3 text-purple-500" />
                        <span className="font-medium">{drawing.createdBy}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <FaCalendar className="w-3 h-3 text-blue-500" />
                        <span>{formatDate(drawing.createdAt)}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <FaPaintBrush className="w-3 h-3 text-green-500" />
                        <span>{drawing.metadata.strokeCount} trazos</span>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => downloadImage(drawing.imageUrl, drawing.name)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-xl hover:from-blue-600 hover:to-teal-600 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                      >
                        <FaDownload className="w-3 h-3" />
                        Descargar
                      </button>
                      
                      <button
                        onClick={() => handleDeleteDrawing(drawing)}
                        disabled={isDeleting === drawing.id}
                        className="flex items-center justify-center px-3 py-2 text-sm bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl hover:from-red-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal para ver dibujo completo */}
      <AnimatePresence>
        {selectedDrawing && (
          <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white/95 backdrop-blur-md rounded-2xl max-w-4xl max-h-[90vh] overflow-auto shadow-2xl border border-white/20"
            >
              {/* Header del modal */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200/50">
                <div>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {selectedDrawing.name}
                  </h2>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <FaUser className="w-3 h-3 text-purple-500" />
                      <span className="font-medium">{selectedDrawing.createdBy}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FaCalendar className="w-3 h-3 text-blue-500" />
                      <span>{formatDate(selectedDrawing.createdAt)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => downloadImage(selectedDrawing.imageUrl, selectedDrawing.name)}
                    className="p-3 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-xl hover:from-blue-600 hover:to-teal-600 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <FaDownload className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => setSelectedDrawing(null)}
                    className="p-3 bg-gray-200/80 text-gray-700 rounded-xl hover:bg-gray-300/80 transition-all duration-200"
                  >
                    <FaTimes className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Imagen del dibujo */}
              <div className="p-6">
                <img
                  src={selectedDrawing.imageUrl}
                  alt={selectedDrawing.name}
                  className="w-full h-auto max-h-[60vh] object-contain rounded-xl shadow-lg"
                />
              </div>

              {/* Información adicional */}
              <div className="p-6 border-t border-gray-200/50 bg-gradient-to-r from-purple-50/50 to-pink-50/50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center p-3 bg-white/60 rounded-xl">
                    <span className="font-bold text-gray-700 block">Dimensiones</span>
                    <p className="text-gray-600 mt-1">
                      {selectedDrawing.metadata.width} × {selectedDrawing.metadata.height}
                    </p>
                  </div>
                  
                  <div className="text-center p-3 bg-white/60 rounded-xl">
                    <span className="font-bold text-gray-700 block">Trazos</span>
                    <p className="text-gray-600 mt-1">
                      {selectedDrawing.metadata.strokeCount}
                    </p>
                  </div>
                  
                  <div className="text-center p-3 bg-white/60 rounded-xl">
                    <span className="font-bold text-gray-700 block">Artista</span>
                    <p className="text-gray-600 mt-1">
                      {selectedDrawing.createdBy}
                    </p>
                  </div>
                  
                  <div className="text-center p-3 bg-white/60 rounded-xl">
                    <span className="font-bold text-gray-700 block">Fecha</span>
                    <p className="text-gray-600 mt-1">
                      {formatDate(selectedDrawing.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};